import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Check,
  ExternalLink,
  FileText,
  Loader2,
  Plus,
  Share2,
  TriangleAlert,
  X,
} from 'lucide-react';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { Textarea } from 'components/ui/textarea';
import { Label } from 'components/ui/label';
import { Alert, AlertDescription } from 'components/ui/alert';
import { ToggleGroup, ToggleGroupItem } from 'components/ui/toggle-group';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'components/ui/tooltip';
import LoadingState from 'components/shared/LoadingState';
import { cn } from 'lib/utils';
import {
  getAdminBlogPosts,
  getYouTubeVideos,
  verifyDiscord,
  postDiscord,
} from '../../services/api';

const PLATFORM_STORAGE_KEY = 'dfc-admin-last-share-platform';

// Brand icons — not available in lucide-react, so tiny inline SVGs.
const TwitterIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={cn('h-4 w-4', className)} aria-hidden="true">
    <path d="M23.643 4.937c-.835.37-1.732.62-2.675.733a4.67 4.67 0 0 0 2.048-2.578 9.3 9.3 0 0 1-2.958 1.13 4.66 4.66 0 0 0-7.938 4.25 13.229 13.229 0 0 1-9.602-4.868c-.4.69-.63 1.49-.63 2.342A4.66 4.66 0 0 0 3.96 9.824a4.647 4.647 0 0 1-2.11-.583v.06a4.66 4.66 0 0 0 3.737 4.568 4.692 4.692 0 0 1-2.104.08 4.661 4.661 0 0 0 4.352 3.234 9.348 9.348 0 0 1-5.786 1.995 9.5 9.5 0 0 1-1.112-.065 13.175 13.175 0 0 0 7.14 2.093c8.57 0 13.255-7.098 13.255-13.254 0-.2-.005-.402-.014-.602a9.47 9.47 0 0 0 2.323-2.41z" />
  </svg>
);

const RedditIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={cn('h-4 w-4', className)} aria-hidden="true">
    <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.91 2.961.91.477 0 2.105-.056 2.961-.91a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" />
  </svg>
);

// ─────────────────────────────────────────────────────────────────────────────
// Share Modal Component
// ─────────────────────────────────────────────────────────────────────────────

const ShareModal = ({ open, onClose, item, type, redditEnabled, discordEnabled }) => {
  const [platform, setPlatform] = useState('twitter');
  const [customText, setCustomText] = useState('');
  const [posting, setPosting] = useState(false);
  const [result, setResult] = useState(null);

  // Reset state when modal opens; restore the last-used platform so the admin
  // doesn't have to switch away from Twitter every time.
  useEffect(() => {
    if (open) {
      setCustomText('');
      setResult(null);
      setPosting(false);
      let initial = 'twitter';
      try {
        const stored = localStorage.getItem(PLATFORM_STORAGE_KEY);
        if (stored === 'discord' && discordEnabled) initial = 'discord';
        else if (stored === 'reddit' && redditEnabled) initial = 'reddit';
      } catch {
        // localStorage unavailable — default stands
      }
      setPlatform(initial);
    }
  }, [open, discordEnabled, redditEnabled]);

  if (!item) return null;

  const changePlatform = (v) => {
    setPlatform(v);
    try {
      localStorage.setItem(PLATFORM_STORAGE_KEY, v);
    } catch {
      // ignore
    }
  };

  const title = item.title;
  const url = type === 'video'
    ? item.url
    : `https://draftfc.co.uk/blog/${item.slug}`;
  const imageUrl = type === 'video'
    ? item.thumbnail
    : item.featured_image_url;

  // Twitter character counting
  const estimatedCharCount = customText
    ? customText.length + 2 + 23  // custom + newlines + shortened URL
    : title.length + 2 + 23;

  const handleShare = async () => {
    setPosting(true);
    setResult(null);

    try {
      if (platform === 'twitter') {
        // Open Twitter Web Intent in new tab (free, no API key required)
        const tweetText = customText || title;
        const intentUrl = `https://x.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(url)}`;
        window.open(intentUrl, '_blank', 'noopener,noreferrer');
        setResult({
          success: true,
          message: 'Twitter compose window opened — confirm the post in the new tab.',
        });
        setPosting(false);
        return;
      } else if (platform === 'discord') {
        const response = await postDiscord({
          text: customText || title,
          url: url,
          title: title,
          image_url: imageUrl || undefined,
        });

        if (response.success) {
          setResult({
            success: true,
            message: 'Posted to Discord!',
            postUrl: response.message_url,
          });
        } else {
          setResult({
            success: false,
            message: response.error || 'Failed to post',
          });
        }
      } else if (platform === 'reddit') {
        // TODO: Implement Reddit API posting
        setResult({
          success: false,
          message: 'Reddit API not yet configured',
        });
      }
    } catch (error) {
      setResult({
        success: false,
        message: error.message || 'Failed to post',
      });
    } finally {
      setPosting(false);
    }
  };

  const shareLabel =
    platform === 'twitter'
      ? 'Share on Twitter'
      : platform === 'discord'
        ? 'Post to Discord'
        : 'Post to Reddit';

  const shareIcon =
    platform === 'twitter' ? (
      <TwitterIcon />
    ) : platform === 'discord' ? (
      <Share2 />
    ) : (
      <RedditIcon />
    );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Share {type === 'video' ? 'Video' : 'Blog Post'}</DialogTitle>
        </DialogHeader>

        {/* Preview Image */}
        {imageUrl && (
          <img
            src={imageUrl}
            alt={title}
            className="h-[180px] w-full rounded-token-large border border-borderGrey object-cover"
          />
        )}

        {/* Title */}
        <div>
          <p className="text-token-md font-medium">{title}</p>
          <p className="break-all text-token-base text-muted-foreground">{url}</p>
        </div>

        {/* Platform Selection — Reddit hidden until it's actually configured */}
        <ToggleGroup
          type="single"
          value={platform}
          onValueChange={(v) => v && changePlatform(v)}
          className="justify-start"
        >
          <ToggleGroupItem
            value="twitter"
            className="gap-1.5 data-[state=on]:border-[#1DA1F2] data-[state=on]:bg-[#E8F5FE] data-[state=on]:text-[#1DA1F2]"
          >
            <TwitterIcon /> Twitter
          </ToggleGroupItem>
          <ToggleGroupItem
            value="discord"
            disabled={!discordEnabled}
            className="gap-1.5 data-[state=on]:border-[#5865F2] data-[state=on]:bg-[#E8EAF6] data-[state=on]:text-[#5865F2]"
          >
            <Share2 className="h-4 w-4" /> Discord
          </ToggleGroupItem>
          {redditEnabled && (
            <ToggleGroupItem
              value="reddit"
              className="gap-1.5 data-[state=on]:border-[#FF4500] data-[state=on]:bg-[#FFF3E0] data-[state=on]:text-[#FF4500]"
            >
              <RedditIcon /> Reddit
            </ToggleGroupItem>
          )}
        </ToggleGroup>

        {!redditEnabled && !discordEnabled && platform !== 'twitter' && (
          <Alert variant="warning">
            <AlertDescription>
              No social platforms configured. Add API credentials to enable posting.
            </AlertDescription>
          </Alert>
        )}

        {/* Custom Message */}
        <div className="grid gap-1.5">
          <Label htmlFor="share-custom-text">
            {platform === 'twitter' ? 'Tweet Text (optional)' : 'Message (optional)'}
          </Label>
          <Textarea
            id="share-custom-text"
            rows={3}
            placeholder={`Default: "${title}"`}
            value={customText}
            onChange={(e) => setCustomText(e.target.value)}
          />
          <p
            className={cn(
              'text-token-base',
              platform === 'twitter' && estimatedCharCount > 280
                ? 'text-destructive'
                : 'text-muted-foreground'
            )}
          >
            {platform === 'twitter'
              ? `Estimated: ${estimatedCharCount}/280 characters (URLs shortened by Twitter)`
              : platform === 'discord'
                ? 'Leave empty to use the content title. Posted as a rich embed.'
                : 'Leave empty to use the content title'}
          </p>
        </div>

        {/* Result Alert */}
        {result && (
          <Alert variant={result.success ? 'success' : 'destructive'}>
            <AlertDescription className="flex items-center justify-between gap-3">
              <span>{result.message}</span>
              {result.postUrl && (
                <Button variant="outline" size="sm" asChild>
                  <a href={result.postUrl} target="_blank" rel="noopener noreferrer">
                    View <ExternalLink />
                  </a>
                </Button>
              )}
            </AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={posting}>
            Close
          </Button>
          <Button
            size="sm"
            onClick={handleShare}
            disabled={
              posting ||
              (platform === 'twitter' && estimatedCharCount > 280) ||
              (platform === 'discord' && !discordEnabled) ||
              (platform === 'reddit' && !redditEnabled)
            }
            className={cn(
              platform === 'twitter' && 'bg-[#1DA1F2] hover:bg-[#1a8cd8]',
              platform === 'discord' && 'bg-[#5865F2] hover:bg-[#4752c4]',
              platform === 'reddit' && 'bg-[#FF4500] hover:bg-[#e03d00]'
            )}
          >
            {posting ? <Loader2 className="animate-spin" /> : shareIcon}
            {posting ? 'Posting...' : shareLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Compact platform status bar (replaces the stack of three full Alerts)
// ─────────────────────────────────────────────────────────────────────────────

const PlatformStatusItem = ({ icon, label, ok, warn, detail }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span className="inline-flex cursor-default items-center gap-1.5 text-token-md">
        {icon}
        {label}
        {ok ? (
          <Check size={14} className="text-success" />
        ) : warn ? (
          <TriangleAlert size={14} className="text-warning" />
        ) : (
          <X size={14} className="text-muted-foreground" />
        )}
      </span>
    </TooltipTrigger>
    <TooltipContent side="bottom" className="max-w-[340px]">
      {detail}
    </TooltipContent>
  </Tooltip>
);

// ─────────────────────────────────────────────────────────────────────────────
// Main Social Share Component
// ─────────────────────────────────────────────────────────────────────────────

export default function SocialShare() {
  const navigate = useNavigate();
  const [view, setView] = useState('videos');
  const [videos, setVideos] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [discordStatus, setDiscordStatus] = useState(null);
  const [redditStatus] = useState({ configured: false, valid: false });

  // Share modal state
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareItem, setShareItem] = useState(null);
  const [shareType, setShareType] = useState(null);

  // Fetch data on mount
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [videosData, postsRes, discordRes] = await Promise.all([
        getYouTubeVideos(),
        getAdminBlogPosts({ status: 'published' }),
        verifyDiscord().catch(() => ({ configured: false, valid: false })),
      ]);

      setVideos(videosData || []);
      setPosts(postsRes.posts || []);
      setDiscordStatus(discordRes);
    } catch (err) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openShareModal = (item, type) => {
    setShareItem(item);
    setShareType(type);
    setShareModalOpen(true);
  };

  if (loading) {
    return <LoadingState message="Loading videos and posts..." />;
  }

  const discordEnabled = discordStatus?.valid === true;
  const redditEnabled = redditStatus?.valid === true;

  return (
    <TooltipProvider delayDuration={200}>
      <div>
        {/* Compact platform status bar — hover an item for detail */}
        <div className="mb-4 flex flex-wrap items-center gap-x-5 gap-y-2 rounded-token-large border border-borderGrey bg-white px-4 py-2.5">
          <span className="text-token-md font-medium text-muted-foreground">
            Platforms
          </span>
          <PlatformStatusItem
            icon={<TwitterIcon className="text-[#1DA1F2]" />}
            label="Twitter"
            ok
            detail="Twitter sharing available via Web Intent (opens in new tab)"
          />
          <PlatformStatusItem
            icon={<Share2 size={16} className="text-[#5865F2]" />}
            label="Discord"
            ok={discordEnabled}
            warn={Boolean(discordStatus?.configured && !discordEnabled)}
            detail={
              discordStatus?.valid
                ? `Discord connected to #${discordStatus.channel_name}`
                : discordStatus?.configured
                  ? `Discord configured but cannot access channel: ${discordStatus.error}`
                  : 'Discord not configured. Set DISCORD_CONTENT_CHANNEL_ID in services/api/.env'
            }
          />
          {redditStatus?.configured && (
            <PlatformStatusItem
              icon={<RedditIcon className="text-[#FF4500]" />}
              label="Reddit"
              ok={redditEnabled}
              detail="Reddit not yet configured. Provide API keys to enable Reddit posting."
            />
          )}
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription className="flex items-center justify-between gap-4">
              <span>{error}</span>
              <Button variant="outline" size="sm" onClick={fetchData}>
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* View Toggle */}
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <ToggleGroup
            type="single"
            value={view}
            onValueChange={(v) => v && setView(v)}
          >
            <ToggleGroupItem value="videos">YouTube ({videos.length})</ToggleGroupItem>
            <ToggleGroupItem value="posts">Blog Posts ({posts.length})</ToggleGroupItem>
          </ToggleGroup>

          {view === 'videos' && (
            <Button variant="outline" size="sm" onClick={() => navigate('/blog/new')}>
              <Plus /> New Blog Post
            </Button>
          )}
        </div>

        {/* Content */}
        {view === 'videos' ? (
          <div className="overflow-auto rounded-token-large border border-borderGrey bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[140px]">Thumbnail</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead className="w-[120px]">Published</TableHead>
                  <TableHead className="w-[200px] text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {videos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <p className="py-8 text-center text-token-md text-muted-foreground">
                        No YouTube videos found
                      </p>
                    </TableCell>
                  </TableRow>
                ) : (
                  videos.map((video) => (
                    <TableRow key={video.id}>
                      <TableCell>
                        <img
                          src={video.thumbnail}
                          alt={video.title}
                          className="h-[68px] w-[120px] rounded-token-medium object-cover"
                        />
                      </TableCell>
                      <TableCell>
                        <p className="mb-0.5 text-token-md font-medium">{video.title}</p>
                        <a
                          href={video.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-token-base text-brand-purple1 hover:underline"
                        >
                          View on YouTube
                        </a>
                      </TableCell>
                      <TableCell>
                        <span className="text-muted-foreground">
                          {video.published
                            ? new Date(video.published).toLocaleDateString()
                            : '–'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1.5">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 w-full"
                            onClick={() => openShareModal(video, 'video')}
                          >
                            <Share2 /> Share
                          </Button>
                          <Button
                            size="sm"
                            className="h-8 w-full"
                            onClick={() => navigate(`/blog/generate?video=${video.id}`)}
                          >
                            <FileText /> Make Blog
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="overflow-auto rounded-token-large border border-borderGrey bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">Image</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead className="w-[120px]">Category</TableHead>
                  <TableHead className="w-[100px]">Published</TableHead>
                  <TableHead className="w-[120px] text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {posts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <p className="py-8 text-center text-token-md text-muted-foreground">
                        No published blog posts
                      </p>
                    </TableCell>
                  </TableRow>
                ) : (
                  posts.map((post) => (
                    <TableRow key={post.id}>
                      <TableCell>
                        {post.featured_image_url ? (
                          <img
                            src={post.featured_image_url}
                            alt={post.title}
                            className="h-[45px] w-[80px] rounded-token-medium object-cover"
                          />
                        ) : (
                          <div className="flex h-[45px] w-[80px] items-center justify-center rounded-token-medium bg-neutral-100">
                            <span className="text-token-xs text-muted-foreground">
                              No image
                            </span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <p className="mb-0.5 text-token-md font-medium">{post.title}</p>
                        <p className="text-token-base text-muted-foreground">
                          /{post.slug}
                        </p>
                      </TableCell>
                      <TableCell>
                        {post.category_name && (
                          <Badge variant="secondary">{post.category_name}</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-muted-foreground">
                          {post.published_at
                            ? new Date(post.published_at).toLocaleDateString()
                            : '–'}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8"
                          onClick={() => openShareModal(post, 'post')}
                        >
                          <Share2 /> Share
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Share Modal */}
        <ShareModal
          open={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          item={shareItem}
          type={shareType}
          discordEnabled={discordEnabled}
          redditEnabled={redditEnabled}
        />

      </div>
    </TooltipProvider>
  );
}
